// @ts-strict-ignore
import { IsThreeJsType } from '@3d-scene/common/util/is-threejs-type';
import { ArticleConnectionPropertyValue } from '@article/constants/article-connection-property-values';
import { StringUtils } from '@shared/util/string-utils';
import { Connection } from '../connection';
import { ConnectionAreaVO, InteractionAreaVO, LightingAreaVO, MetaElementVO, MontagePointAreaVO, ReflectionAreaVO, SimpleTextValueVO, TextureAreaVO, TextValueVO } from '@shared/vo/meta-element.vo';
import { RangeOnLineConnectionTypeDef } from '@project/constants/range-on-line-connection-type-def';
import { ArrayUtils } from '@shared/util/array-utils';
import { PropertyUtils } from '@article/util/property-utils';
import { MetaElementBehaviorPropertyDef } from '@project/constants/meta-element-behavior-property-def';
import { PropertyVO } from '@project/components/article/model/vo/property.vo';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ArticleUtils } from '@article/util/article-utils';
import { Rectangle3D } from '../../model/rectangle-3d';
import { Euler, Mesh, MeshPhongMaterial, Object3D, Vector3 } from 'three';
import { MathUtils } from '../../common/util/math-utils';
import { PointsPair } from '../../model/points-pair';
import { ArticleConstants } from '@article/constants/article-constants';
import { CommonConstants } from '../../constants/common-constants';
import { Line } from '../../model/line';
import { ArticleContainer } from '../article-container';
import { ArticleConnectionPropertyDef } from '@article/constants/article-connection-property-def';
import { Injectable } from '@angular/core';
import { Face3D } from '../../model/face-3d';
import { ConnectionSortGroup } from '../connection-sort-group';
import { MetaElementAccessoryCategoryDef } from '@project/constants/meta-element-accessory-category-def';
import { MetaElementUtils } from '@article/components/connections/connection-creation/meta-element.utils';
import { DefinedMetaElementInfoPropertyDef } from '@project/constants/defined-meta-element-info-property-def';
import { CloneUtils } from '@shared/util/clone-utils';
import { MeshUtils } from '../../common/util/mesh-utils';
import { MetaElementUsageUtils } from '@project/util/meta-element-usage-utils';
import { ConnectionAreaUsageUtils } from '@shared/util/connection-area-usage-utils';
import { Object3DUtils } from '../../common/util/object-3d-utils';

@Injectable({providedIn: 'root'})
export class ConnectionInfoUtils {

  public static isTextConnectionType(connectionType: string): boolean {
    return Connection.TEXT_TYPES.indexOf(connectionType) !== -1;
  }

  public static isConnectionOfType(connection: Connection, connectionTypes: string[]): boolean {
    if (!connection || !connectionTypes) {
      return false;
    }
    return connectionTypes.indexOf(connection.type) >= 0;
  }

  public static isConnectionDisplayed(metaElement: MetaElementVO): boolean {
    if (!metaElement) {
      return false;
    }
    return !this.isDrillingHoleRangeOnLineConnection(metaElement);
  }

  public static isDrillingHoleRangeOnLineConnection(metaElement: MetaElementVO): boolean {
    if (!metaElement) {
      return false;
    }
    return this.isRangeOnLineConnectionByType(metaElement, RangeOnLineConnectionTypeDef.TYPE_DRILLING_HOLE);
  }

  public static isDefaultRangeOnLineConnection(metaElement: MetaElementVO): boolean {
    if (!metaElement) {
      return false;
    }
    return this.isRangeOnLineConnectionByType(metaElement);
  }

  public static isRangeOnLineConnectionByType(metaElement: MetaElementVO, type: string = null): boolean {
    if (!metaElement) {
      return false;
    }
    return this.isRangeOnLineConnection(metaElement) && type === this.getRangeOnLineConnectionType(metaElement);
  }

  /**
   * Method checks whether the connection that contains given metaElement is a range-on-line connection (uFreedom == 0).
   *
   * @param metaElement meta element which needs to be checked
   * @return true if connection is range-on-line, otherwise false
   *
   */
  public static isRangeOnLineConnection(metaElement: MetaElementVO): boolean {
    if (!metaElement) {
      return false;
    }
    return (
      metaElement instanceof ConnectionAreaVO &&
      (metaElement as ConnectionAreaVO).connectionFreedom &&
      (metaElement as ConnectionAreaVO).connectionFreedom.uFreedom === 0
    );
  }

  public static findDefaultRangeOnLineConnection(metaElements: MetaElementVO[]): ConnectionAreaVO {
    return this.findRangeOnLineConnectionByType(metaElements, null);
  }

  public static findRangeOnLineConnectionByType(metaElements: MetaElementVO[], type: string): ConnectionAreaVO {
    if (ArrayUtils.isEmpty(metaElements)) {
      return null;
    }

    for (const meta of metaElements) {
      if (this.isRangeOnLineConnectionByType(meta, type)) {
        return meta as ConnectionAreaVO;
      }
    }
    return null;
  }



  public static getRangeOnLineConnectionType(metaElement: MetaElementVO): string {
    if (!metaElement) {
      return null;
    }
    return PropertyUtils.getStringPropertyValue(metaElement.behaviorProperties, MetaElementBehaviorPropertyDef.RANGE_ON_LINE_TYPE);
  }

  public static findRangeOnLineConnections(metaElements: MetaElementVO[]): MetaElementVO[] {
    if (ArrayUtils.isEmpty(metaElements)) {
      return [];
    }
    const rangeOnLineConnections: MetaElementVO[] = [];
    for (const meta of metaElements) {
      if (this.isRangeOnLineConnection(meta)) {
        rangeOnLineConnections.push(meta);
      }
    }
    return rangeOnLineConnections;
  }

  public static isImageConnections(connections: Connection[]): boolean {
    return this.isAllConnectionsOfType(connections, [Connection.TYPE_IMAGE]);
  }

  public static isAllConnectionsOfType(connections: Connection[], connectionTypes: string[]): boolean {
    for (const connection of connections) {
      if (!this.isConnectionOfType(connection, connectionTypes)) {
        return false;
      }
    }
    return true;
  }

  public static cloneProperties(properties: PropertyVO[]): PropertyVO[] {
    if (!properties || !properties.length) {
      return [];
    }
    const clonedProperties: PropertyVO[] = [];
    properties.forEach((property: PropertyVO) => clonedProperties.push(property.clone()));
    return clonedProperties;
  }

  public static getConnectionSetIdByConnectionName(article: ArticleVO, metaElementName: string): string {
    if (!article || !metaElementName) {
      return null;
    }
    const metaElement: MetaElementVO = ArticleUtils.findMetaElementByName(article, metaElementName);
    return MetaElementUtils.getConnectionSetId(metaElement);
  }

  /**
   * Method calculates common rectangle for rectangles. If inverse is set to false then line between point1 and point4
   * defines "U" direction. Otherwise its "V" direction.
   *
   * @param rectangles
   * @param inverse if the rectangle should be created for accepting connections then flag must be set to false (if giving - then true)
   * @param useSortFunction if sort rectangles should be sorted by middle point. Otherwise first connection from given array will be used
   * as closest connection, last connection - as farthest
   * @return common rectangle
   *
   */
  public static calculateCommonRectangle(rectangles: Rectangle3D[], inverse: boolean = false, useSortFunction: boolean = false): Rectangle3D {
    if (rectangles.length === 1) {
      return rectangles[0];
    }
    const sortedRectangles: Rectangle3D[] = useSortFunction ? this.sortRectangles(rectangles) : rectangles;
    const closestRect: Rectangle3D = sortedRectangles[0];
    const farestRect: Rectangle3D = sortedRectangles[sortedRectangles.length - 1];

    const closestPoints: Vector3[] = closestRect.toArray();
    const farestPoints: Vector3[] = []; // farestConnectionCube.rectangle.toArray();
    for (const point of farestRect.toArray()) {
      farestPoints.push(MathUtils.getProjectionPointOfPlane(closestRect.point1, closestRect.point2, closestRect.point3, point));
    }

    const pointPairs: PointsPair[] = Object3DUtils.findPointPairs(closestPoints, farestPoints);

    /* Point pairs is a pair of points, which resides on different connections/articles, for example
     *
     * |a1   a4|  |b1   b4|
     * |       |  |       |
     * |a2   a3|  |b2   b3|
     *
     * The farest pairs will be (a1, b3) and (a2, b4). And always first point in pair will be point on
     * the first article, and second point - point of the second article.
     */
    const pair1: PointsPair = pointPairs[0];
    const pair2: PointsPair = pointPairs[1];

    /*
     * Rectangle sides are left and right rectangle borders: (a1, a2) and (b3, b4)
     */
    const line1: Line = this.calculateRectangleSideFromRectangles(pair1.startPoint, pair2.startPoint, sortedRectangles);
    const line2: Line = this.calculateRectangleSideFromRectangles(pair1.endPoint, pair2.endPoint, sortedRectangles);

    const rectangleNormale: Vector3 = MathUtils.getNormalFromFirstPoint(line1.start, line1.end, line2.start);
    const connectionNormale: Vector3 = MathUtils.getNormalFromFirstPoint(closestRect.point1, closestRect.point2, closestRect.point3);

    if (!MathUtils.equalsNumber3D(rectangleNormale, connectionNormale)) {
      line1.reverse();
      line2.reverse();
    }

    /**     Z
     *      ^
     *      |
     *      |
     * X <--------------
     *
     * If (inverse) {
     *
     * |P4     P3|
     * |         |
     * |P1     P2|
     *
     * } else {
     *
     * |P1     P4|
     * |         |
     * |P2     P3|
     *
     * }
     */

    const commonRectangle: Rectangle3D = inverse
      ? Rectangle3D.fromArray([line1.end, line2.start, line2.end, line1.start])
      : Rectangle3D.fromArray([line1.start, line1.end, line2.start, line2.end]);
    return commonRectangle;
  }

  public static sortRectangles(rectangles: Rectangle3D[], pointToSort: Vector3 = null, useOriginPoint: boolean = false): Rectangle3D[] {
    return this.sortByRectangles(rectangles, pointToSort, useOriginPoint, function (rectangle: Rectangle3D): Rectangle3D {
      return rectangle;
    });
  }

  public static calculateRectangleSideFromRectangles(startPoint: Vector3, endPoint: Vector3, rectangles: Rectangle3D[]): Line {
    return this.internalCalculateRectangleSide(startPoint, endPoint, rectangles, function (rectangle: Rectangle3D): Vector3[] {
      return rectangle.toArray();
    });
  }

  /**
   * Sort given connection cubes by constant point.
   *
   * @param connectionCubes connection cubes
   * @param pointToSort [optional] point from which connection cubes will be sorted
   * @param useOriginPoint [optional] use origin (first) point instead of middle point to sort connections
   * @return sorted connection cubes
   *
   */
  public static sortConnectionsByPoint(connectionCubes: Connection[], pointToSort: Vector3 = null, useOriginPoint: boolean = false): Connection[] {
    return this.sortByRectangles(connectionCubes, pointToSort, useOriginPoint, function (connection: Connection): Rectangle3D {
      return connection.compositionRectangle;
    });
  }

  /**
   * Method calculates line that contains projected rectangle points of provided cubes on line between start and end point.
   *
   * @param startPoint start point of original line
   * @param endPoint end point of original line
   * @param selectedConnections array of connection cubes which are used for calculating projection
   * @return resulting line
   */
  public static calculateRectangleSide(startPoint: Vector3, endPoint: Vector3, selectedConnections: Connection[]): Line {
    return this.internalCalculateRectangleSide(startPoint, endPoint, selectedConnections, function (connection: Connection): Vector3[] {
      return connection.compositionRectangle.toArray();
    });
  }

  public static getBusbarConnections(articleContainer: ArticleContainer): Connection[] {
    const acceptingConnections: Connection[] = this.getAcceptingConnections([articleContainer]);
    return this.getBusbarConnectionsFromAcceptingConnections(acceptingConnections);
  }

  public static getAcceptingConnections(articleContainers: ArticleContainer[]): Connection[] {
    return this.getConnections(articleContainers, ConnectionAreaVO.SIDE_ACCEPTING);
  }

  public static getConnections(articleContainers: ArticleContainer[], side: string = null): Connection[] {
    const connections: Connection[] = [];
    for (const container of articleContainers) {
      const connectionAreas: ConnectionAreaVO[] = ArticleUtils.getConnectionElements(container.article, side);
      for (const connectionArea of connectionAreas) {
        const connection: Connection = container.findConnectionByName(connectionArea.name);
        if (connection) {
          connections.push(connection);
        }
      }
    }
    return connections;
  }

  private static sortByRectangles<T>(items: T[], pointToSort: Vector3, useOriginPoint: boolean, getRectangleFunction: Function): T[] {
    if (!pointToSort) {
      pointToSort = ArticleConstants.CONSTANT_POINT;
    }

    const sortedItems: T[] = [];
    let itemAdded: boolean;
    for (let i: number = 0; i < items.length; i++) {
      itemAdded = false;

      if (sortedItems.length === 0) {
        sortedItems.push(items[i]);
      } else {
        const cubeRect: Rectangle3D = getRectangleFunction(items[i]);
        const connectionPoint: Vector3 = useOriginPoint ? cubeRect.point1 : MathUtils.calculateMiddlePointByRectangle(cubeRect);

        for (let j: number = 0; j < sortedItems.length; j++) {
          const sortedRect: Rectangle3D = getRectangleFunction(sortedItems[j]);
          const sortedConnectionPoint: Vector3 = useOriginPoint ? sortedRect.point1 : MathUtils.calculateMiddlePointByRectangle(sortedRect);
          if (pointToSort.distanceTo(connectionPoint) < pointToSort.distanceTo(sortedConnectionPoint)) {
            sortedItems.splice(sortedItems.indexOf(sortedItems[j]), 0, items[i]);
            itemAdded = true;
            break;
          }
        }
        if (!itemAdded) {
          sortedItems.push(items[i]);
        }
      }
    }
    return sortedItems;
  }

  private static internalCalculateRectangleSide<T>(startPoint: Vector3, endPoint: Vector3, items: T[], getRectanglePoints: Function): Line {
    const interpolatedStartPoint: Vector3 = MathUtils.getInterpolated(
      startPoint,
      endPoint,
      CommonConstants.CONSTANT_OFFSET / startPoint.distanceTo(endPoint)
    );
    const interpolatedEndPoint: Vector3 = MathUtils.getInterpolated(
      endPoint,
      startPoint,
      CommonConstants.CONSTANT_OFFSET / startPoint.distanceTo(endPoint)
    );
    const line: Line = new Line(interpolatedStartPoint, interpolatedEndPoint);

    let closestPoint: Vector3;
    let farestPoint: Vector3;

    for (let i: number = 0; i < items.length; i++) {
      const rectanglePoints: Vector3[] = getRectanglePoints(items[i]);
      for (const rectanglePoint of rectanglePoints) {
        const pointOnLine: Vector3 = MathUtils.closestPointOnLine(line.start, line.end, rectanglePoint);
        if (!closestPoint) {
          closestPoint = pointOnLine;
          farestPoint = pointOnLine;
        } else {
          if (line.start.distanceTo(closestPoint) > line.start.distanceTo(pointOnLine)) {
            closestPoint = pointOnLine;
          }
          if (line.end.distanceTo(farestPoint) > line.end.distanceTo(pointOnLine)) {
            farestPoint = pointOnLine;
          }
        }
      }
    }

    return new Line(closestPoint, farestPoint);
  }

  public static getTextValueByTextureArea(textureArea: TextureAreaVO): TextValueVO {
    if (!textureArea) {
      return null;
    }
    let defaultTextValue: SimpleTextValueVO = textureArea.defaultValueAsText;
    let defaultText: string = !defaultTextValue ? null : defaultTextValue.text;
    if (defaultText) {
      return defaultTextValue;
    }
    defaultTextValue = null;
    defaultText = this.getDefaultText(textureArea);

    if (defaultText) {
      defaultTextValue = new SimpleTextValueVO();
      defaultTextValue.text = defaultText;
      defaultTextValue.rotation = this.getDefaultTextRotation(textureArea);
      defaultTextValue.vAlignment = this.getDefaultTextVerticalAlignment(textureArea);
    }
    return defaultTextValue;
  }

  public static getDefaultText(metaElement: MetaElementVO): string {
    if (!metaElement) {
      return null;
    }
    return PropertyUtils.getStringPropertyValue(metaElement.behaviorProperties, MetaElementBehaviorPropertyDef.DEFAULT_TEXT);
  }

  public static getDefaultTextRotation(metaElement: MetaElementVO): number {
    if (!metaElement) {
      return 0;
    }
    return PropertyUtils.getIntPropertyValue(metaElement.behaviorProperties, MetaElementBehaviorPropertyDef.DEFAULT_TEXT_ROTATION) || 0;
  }

  public static getDefaultTextVerticalAlignment(metaElement: MetaElementVO): number {
    if (!metaElement) {
      return 0;
    }
    return PropertyUtils.getIntPropertyValue(metaElement.behaviorProperties, MetaElementBehaviorPropertyDef.DEFAULT_TEXT_VERTICAL_ALIGNMENT) || 0;
  }

  public static isStaticMetaElement(metaElement: MetaElementVO): boolean {
    if (!metaElement) {
      return false;
    }
    return PropertyUtils.getBooleanPropertyValue(metaElement.behaviorProperties, MetaElementBehaviorPropertyDef.IS_STATIC_CONNECTION);
  }

  /**
   * Searches giving connection area in article that can be connected to parent (accepting) connection.
   *
   * @param article article in which to search giving connection
   * @param parentArticle parent article that contains parent connection area
   * @param parentConnection parent connection area
   * @return connection area tha can be connected to the parent article. If connection not found then <code>null</code> is returned
   */
  public static findRequiredConnection(article: ArticleVO, parentArticle: ArticleVO, parentConnection: ConnectionAreaVO): ConnectionAreaVO {
    const allRequiredConnections: ConnectionAreaVO[] = this.findRequiredConnections(article, parentArticle, parentConnection, true);
    return allRequiredConnections[0];
  }

  public static findRequiredConnections(
    article: ArticleVO,
    parentArticle: ArticleVO,
    parentConnection: ConnectionAreaVO,
    findFirstConnection: boolean = false
  ): ConnectionAreaVO[] {
    if (!article || !parentArticle || !parentConnection) {
      return [];
    }
    const result: ConnectionAreaVO[] = [];
    const acceptingProperties: PropertyVO[] = this.createRequirementPropertyList(parentConnection, parentArticle);
    for (const metaElement of article.metaElements) {
      if (metaElement instanceof ConnectionAreaVO && metaElement.connectionSide === ConnectionAreaVO.SIDE_GIVING) {
        const givingProperties: PropertyVO[] = this.createFeaturePropertyList(metaElement, article);
        if (PropertyUtils.checkProperties(givingProperties, acceptingProperties)) {
          result.push(metaElement);
          if (findFirstConnection) {
            return result;
          }
        }
      }
    }
    return result;
  }

  /**
   * Create list with requirement properties from connection and the article that contains the connection.
   * If connection and article contain properties with the same names, then result array will only contain the property from the connection.
   * @param connection connection area
   * @param article article that contains the connection
   * @return array of requirement properties
   *
   */
  public static createRequirementPropertyList(connection: ConnectionAreaVO, article: ArticleVO): PropertyVO[] {
    let properties: PropertyVO[] = [];
    if (connection && connection.connectionRequirements && connection.connectionRequirements.length > 0) {
      properties = connection.connectionRequirements.concat();
    }
    if (article != null && article.connectionRequirements != null && article.connectionRequirements.length > 0) {
      for (const articleProperty of article.connectionRequirements) {
        const connProperty: PropertyVO = PropertyUtils.getPropertyByName(properties, articleProperty.name);
        if (!connProperty) {
          properties.push(articleProperty);
        }
      }
    }
    return properties;
  }

  /**
   * Create list with feature properties from connection and the article that contains the connection.
   * If connection and article contain properties with the same names, then result array will only contain the property from the connection.
   * @param connection connection area
   * @param article article that contains the connection
   * @return array of feature properties
   */
  public static createFeaturePropertyList(connection: ConnectionAreaVO, article: ArticleVO): PropertyVO[] {
    let properties: PropertyVO[] = [];
    if (connection && connection.connectionFeatures && connection.connectionFeatures.length > 0) {
      properties = connection.connectionFeatures.concat();
    }
    if (article.connectionFeatures && article.connectionFeatures.length > 0) {
      for (const articleProperty of article.connectionFeatures) {
        const connProperty: PropertyVO = PropertyUtils.getPropertyByName(properties, articleProperty.name);
        if (!connProperty) {
          properties.push(articleProperty);
        }
      }
    }
    return properties;
  }

  /**
   * Retrieves array of points from given faces and transform them to container position.
   *
   * @param faces faces which contains necessary points
   * @param container whose coordinates are used for transformation
   * @return array of points
   */
  public static getPointsFromFaces(faces: Face3D[], container: ArticleContainer): Vector3[] {
    const points: Vector3[] = [];
    faces.forEach((data: Face3D) => {
      for (const vertex of data.vertices) {
        const point: Vector3 = container.localToWorld(vertex.clone());
        if (!MathUtils.isArrayContainsVector3D(points, point)) {
          points.push(point);
        }
      }
    });
    return points;
  }

  /**
   * Checks whether given meta-element is default labeling.
   * Meta-element considered as default labeling in case it doesn't contain isNotPartOfDefaultLabeling behavior-property.
   * So, for example, all texture areas are default labelings by default (in case isNotPartOfDefaultLabeling-property is not specified).
   */
  public static isPartOfDefaultLabeling(metaElement: MetaElementVO): boolean {
    if (!metaElement) {
      return false;
    }
    const isNotPartOfDefaultLabeling: boolean = PropertyUtils.getBooleanPropertyValue(
      metaElement.behaviorProperties,
      MetaElementBehaviorPropertyDef.IS_NOT_PART_OF_DEFAULT_LABELING
    );
    return !isNotPartOfDefaultLabeling;
  }

  /**
   * Sort given connection cubes by u-direction.
   *
   * @param connections connection cubes to sort
   * @param inverse true if need to sort connection-cubes by v-direction, otherwise u-direction will be used
   * @return sorted connection cubes
   */
  public static sortConnections(connections: Connection[], inverse: boolean = false): Connection[] {
    if (ArrayUtils.isEmpty(connections) || connections.length <= 1) {
      return connections;
    }
    const firstConnectionRectangle: Rectangle3D = connections[0].compositionRectangle;
    const sortDirection: Vector3 = inverse
      ? firstConnectionRectangle.point2.clone().sub(firstConnectionRectangle.point1)
      : firstConnectionRectangle.point4.clone().sub(firstConnectionRectangle.point1);
    sortDirection.normalize();

    const normal: Vector3 = MathUtils.getNormalFromFirstPoint(
      firstConnectionRectangle.point1,
      firstConnectionRectangle.point2,
      firstConnectionRectangle.point3
    );

    const fittingGroup: ConnectionSortGroup = new ConnectionSortGroup();
    fittingGroup.sortDirection = sortDirection;
    fittingGroup.normalDirection = normal;
    fittingGroup.connectionCubes = connections.concat();
    fittingGroup.sortConnections();
    return fittingGroup.connectionCubes;
  }

  public static getConnectionVDirection(connection: Connection, inGlobal: boolean = true): Vector3 {
    const rectangle: Rectangle3D = inGlobal ? connection.globalRectangle : connection.localRectangle;
    return rectangle.point4.clone().sub(rectangle.point1).normalize();
  }

  public static hasDynamicPinReference(metaElement: MetaElementVO): boolean {
    if (!metaElement) {
      return false;
    }
    return StringUtils.isNotEmpty(this.getDynamicPinReference(metaElement));
  }

  public static getDynamicPinReference(metaElement: MetaElementVO): string {
    return PropertyUtils.getStringPropertyValue(metaElement.behaviorProperties, MetaElementBehaviorPropertyDef.DYNAMIC_PIN_REFERENCE);
  }

  public static isConnectionSelectable(metaElement: MetaElementVO): boolean {
    return !PropertyUtils.getBooleanPropertyValue(metaElement.behaviorProperties, MetaElementBehaviorPropertyDef.CONNECTION_IS_NOT_SELECTABLE);
  }

  public static hasLabelingAccessoryCategory(metaElement: MetaElementVO): boolean {
    if (!metaElement) {
      return false;
    }
    return this.hasAccessoryCategory(metaElement, MetaElementAccessoryCategoryDef.ACCESSORY_CATEGORY_LABELING);
  }

  public static hasAccessoryCategory(metaElement: MetaElementVO, category: string): boolean {
    if (!metaElement || !category) {
      return false;
    }
    return this.getConnectionAccessoryCategories(metaElement).includes(category);
  }

  public static getConnectionAccessoryCategories(metaElement: MetaElementVO): string[] {
    if (!metaElement) {
      return [];
    }
    return PropertyUtils.getStringPropertyValues(metaElement.behaviorProperties, MetaElementBehaviorPropertyDef.ACCESSORY_CATEGORY);
  }

  /**
   * Returns all connections in the given article containers.
   */
  public static findConnectionsInArticles(articleContainers: ArticleContainer[]): Connection[] {
    const connections: Connection[] = [];
    for (const articleContainer of articleContainers) {
      for (const meta of articleContainer.article.metaElements) {
        const connection: Connection = articleContainer.findConnectionByName(meta.name);
        if (connection) {
          connections.push(connection);
        }
      }
    }
    return connections;
  }

  public static getSelectionSetIdsByConnection(connection: Connection): string[] {
    if (!connection) {
      return [];
    }
    return this.getSelectionSetIds(connection.metaElementVO);
  }

  public static getSelectionSetIds(metaElement: MetaElementVO): string[] {
    if (!metaElement) {
      return [];
    }
    return PropertyUtils.getStringPropertyValues(metaElement.behaviorProperties, MetaElementBehaviorPropertyDef.SELECTION_SET_ID);
  }

  public static getFreeAcceptingConnections(articleContainer: ArticleContainer): Connection[] {
    if (!articleContainer) {
      return [];
    }
    const cubes: Connection[] = [];
    const freeConnections: ConnectionAreaVO[] = this.getFreeConnectionAreas(articleContainer, ConnectionAreaVO.SIDE_ACCEPTING);
    for (const connectionArea of freeConnections) {
      const cube: Connection = articleContainer.findFirstConnectionByName(connectionArea.name);
      if (cube) {
        cubes.push(cube);
      }
    }
    return cubes;
  }

  /**
   * Returns free connection areas by the given article container.
   */
  public static getFreeConnectionAreas(container: ArticleContainer, side: string = null): ConnectionAreaVO[] {
    const freeAreas: ConnectionAreaVO[] = [];
    if (container) {
      let connectionAreas: ConnectionAreaVO[];
      if (ConnectionAreaVO.SIDE_GIVING === side) {
        connectionAreas = ArticleUtils.getGivingConnectionsInActiveSet(container.article, container.articleUsage);
      } else {
        connectionAreas = ArticleUtils.getConnectionElements(container.article, side);
      }

      for (const connectionArea of connectionAreas) {
        const connectionAreaUsage = ArticleUsageUtils.findConnectionAreaUsageByName(container.articleUsage, connectionArea.name);
        if (connectionAreaUsage) {
          if (ConnectionAreaUsageUtils.isFreeConnectionUsage(connectionAreaUsage, connectionArea.connectionMultiplicity)) {
            freeAreas.push(connectionArea);
          }
          // An accepting connectionArea that has no usage (is not used) is a free connectionArea and has to be included.
          // A giving connectionArea has no usage and has to be included.
        } else if (side === connectionArea.connectionSide) {
          freeAreas.push(connectionArea);
        }
      }
    }

    return freeAreas;
  }

  public static checkConnectionPositions(givingConnection: Connection, acceptingConnection: Connection): boolean {
    return (
      this.checkUDistance(givingConnection, acceptingConnection) &&
      this.checkVDistance(givingConnection, acceptingConnection) &&
      this.checkNDistance(givingConnection, acceptingConnection)
    );
  }

  private static checkUDistance(givingConnection: Connection, acceptingConnection: Connection): boolean {
    const givingRectangle: Rectangle3D = givingConnection.compositionRectangle;
    const givingLine: Line = new Line(givingRectangle.point1, givingRectangle.point4);
    if (givingConnection.metaElementVO instanceof ConnectionAreaVO) {
      const delta: number = PropertyUtils.getDeltaValue(givingConnection.metaElementVO, CommonConstants.DIRECTION_U);
      const connectionDistance: number = MathUtils.calculateConnectionRecsDistanceOnLine(
        givingConnection.compositionRectangle,
        acceptingConnection.compositionRectangle,
        givingLine
      );

      return Math.abs(connectionDistance) <= delta + CommonConstants.EPSILON;
    }
    return false;
  }

  private static checkVDistance(givingConnection: Connection, acceptingConnection: Connection): boolean {
    const givingRectangle: Rectangle3D = givingConnection.compositionRectangle;
    const givingLine: Line = new Line(givingRectangle.point1, givingRectangle.point2);
    if (givingConnection.metaElementVO instanceof ConnectionAreaVO) {
      const delta: number = PropertyUtils.getDeltaValue(givingConnection.metaElementVO, CommonConstants.DIRECTION_V);
      const connectionDistance: number = MathUtils.calculateConnectionRecsDistanceOnLine(
        givingConnection.compositionRectangle,
        acceptingConnection.compositionRectangle,
        givingLine
      );

      return Math.abs(connectionDistance) <= delta + CommonConstants.EPSILON;
    }
    return false;
  }

  private static checkNDistance(givingConnection: Connection, acceptingConnection: Connection): boolean {
    const givingRectangle: Rectangle3D = givingConnection.compositionRectangle;
    const givingNormal: Vector3 = MathUtils.getNormalFromFirstPoint(givingRectangle.point1, givingRectangle.point2, givingRectangle.point3);
    givingNormal.add(givingRectangle.point1);
    const givingLine: Line = new Line(givingRectangle.point1, givingNormal);
    if (givingConnection.metaElementVO instanceof ConnectionAreaVO) {
      const delta: number = PropertyUtils.getDeltaValue(givingConnection.metaElementVO, CommonConstants.DIRECTION_N);
      const connectionDistance: number = MathUtils.calculateConnectionRecsDistanceOnLine(
        givingConnection.compositionRectangle,
        acceptingConnection.compositionRectangle,
        givingLine
      );

      return connectionDistance >= -CommonConstants.EPSILON && connectionDistance <= delta + CommonConstants.EPSILON;
    }
    return false;
  }

  public static isGivingConnection(article: ArticleVO, connectionName: string): boolean {
    for (const metaElement of article.metaElements) {
      if (metaElement.name === connectionName) {
        return metaElement instanceof ConnectionAreaVO && (metaElement as ConnectionAreaVO).connectionSide === ConnectionAreaVO.SIDE_GIVING;
      }
    }
    return false;
  }

  /**
   * Searches all giving connection areas in article that can be connected to parent (accepting) connection.
   *
   * @param article article in which to search giving connection
   * @param parentArticle parent article that contains parent connection area
   * @param parentConnection parent connection area
   * @return connection areas tha can be connected to the parent article. If connection not found then <code>null</code> is returned
   *
   */
  public static findAllRequiredConnections(article: ArticleVO, parentArticle: ArticleVO, parentConnection: ConnectionAreaVO): ConnectionAreaVO[] {
    return this.findRequiredConnections(article, parentArticle, parentConnection);
  }

  /**
   * Returns free accepting connection cubes
   */
  public static filterAcceptingCubes(connectionCubes: Connection[], ignoreMultiplicity: boolean = false): Connection[] {
    const acceptingCubes: Connection[] = [];
    for (const cube of connectionCubes) {
      let processedCube: Connection = this.findAcceptingMultipurposeCubeByTextureCube(cube);
      if (!processedCube) {
        processedCube = cube;
      }

      const connectionArea: ConnectionAreaVO = processedCube.metaElementVO as ConnectionAreaVO;

      if (!connectionArea || acceptingCubes.indexOf(processedCube) > -1) {
        continue;
      }

      if (this.isFreeConnection(processedCube, ignoreMultiplicity)) {
        acceptingCubes.push(processedCube);
      }
    }
    return acceptingCubes;
  }

  /**
   * If there are selection sets for this connection, all the connections of the connections sets are also checked.
   * If not only the connection itself is checked.
   * @param connection The connection to check.
   * @param ignoreMultiplicity Indicates whether the multiplicity of the connection should be checked or ignored.
   */
  public static isFreeConnection(connection: Connection, ignoreMultiplicity: boolean): boolean {
    const selectionsSetIds = this.getSelectionSetIdsByConnection(connection);
    if (selectionsSetIds.length === 0) {
      // No selection sets, therefore only check connection itself.
      const isFreeConnection = this.isFreeConnectionInternal(connection, ignoreMultiplicity);
      return isFreeConnection;
    }

    // Check all connections of the connection sets.
    const articleConnections = connection.articleContainer.connections;
    for (let articleConnection of articleConnections) {
      const selectionsSetIds2 = this.getSelectionSetIdsByConnection(articleConnection);
      if (selectionsSetIds.some(value => selectionsSetIds2.includes(value))) {
        if (!this.isFreeConnectionInternal(articleConnection, ignoreMultiplicity)) {
          return false;
        }
      }
    }
    return true;
  }

  private static isFreeConnectionInternal(connection: Connection, ignoreMultiplicity: boolean) {
    return connection.metaElementVO instanceof ConnectionAreaVO &&
      connection.metaElementVO.connectionSide === ConnectionAreaVO.SIDE_ACCEPTING &&
      MetaElementUsageUtils.isFreeConnectionUsage(
        connection.metaElementUsage,
        ignoreMultiplicity ? 1 : connection.metaElementVO.connectionMultiplicity
      );
  }

  public static findAcceptingMultipurposeCubeByTextureCube(cube: Connection): Connection {
    let acceptingMultipurposeCube: Connection = null;
    if (cube.metaElementVO instanceof TextureAreaVO) {
      const linkedCube: Connection = this.findLinkedMultipurposeConnectionCube(cube);
      if (linkedCube) {
        acceptingMultipurposeCube = linkedCube;
      }
    }
    return acceptingMultipurposeCube;
  }

  public static findLinkedMultipurposeConnectionCube(connectionCube: Connection): Connection {
    if (!connectionCube) {
      return null;
    }
    const articleContainer: ArticleContainer = connectionCube.articleContainer;
    if (!articleContainer) {
      return null;
    }
    const linkedMetaElement: MetaElementVO = this.findLinkedMultipurposeMetaElement(articleContainer.article, connectionCube.metaElementVO);
    return !linkedMetaElement ? null : articleContainer.findFirstConnectionByName(linkedMetaElement.name);
  }

  public static findLinkedMultipurposeMetaElement(article: ArticleVO, metaElement: MetaElementVO): MetaElementVO {
    if (!article || !metaElement) {
      return null;
    }

    const linkedMetaElementName = metaElement.name.endsWith(MetaElementUtils.INTERNAL_CONNECTION_POSTFIX)
      ? MetaElementUtils.removeInternalConnectionPostfix(metaElement.name)
      : MetaElementUtils.applyInternalConnectionPostfix(metaElement.name);
    return ArticleUtils.findMetaElementByName(article, linkedMetaElementName);
  }

  public static removeImagesFromConnectionCubes(imageConnectionCubes: Connection[]): void {
    for (const connection of imageConnectionCubes) {
      ArticleUsageUtils.removeDefinedMetaElementInfoPropertyByMetaElementUsageAndPropertyName(
        connection.articleContainer.articleUsage,
        connection.connectionName,
        DefinedMetaElementInfoPropertyDef.CUSTOM_IMAGE_HASH
      );
      connection.resetTexture();
    }
  }

  public static updateImageHashForConnections(imageHash: string, connections: Connection[]): void {
    for (const connection of connections) {
      const articleUsage: ArticleUsageVO = connection.articleContainer.articleUsage;
      if (!articleUsage.definedMetaElementInfoProperties) {
        articleUsage.definedMetaElementInfoProperties = [];
      }
      ArticleUsageUtils.setDefinedMetaElementInfoPropertyStringValue(
        articleUsage,
        connection.connectionName,
        DefinedMetaElementInfoPropertyDef.CUSTOM_IMAGE_HASH,
        imageHash
      );
      connection.resetTexture();
    }
  }

  public static getConnectionMeshes(object3D: Object3D, connectionNames: string[]): Mesh[] {
    const connectionMeshes: Mesh[] = [];
    if (!object3D || ArrayUtils.isEmpty(connectionNames)) {
      return connectionMeshes;
    }
    if (IsThreeJsType.mesh(object3D)) {
      if (connectionNames.indexOf(object3D.name) >= 0) {
        connectionMeshes.push(object3D);
      }
    } else {
      object3D.children.forEach((child: Object3D) => {
        ArrayUtils.addAll(connectionMeshes, this.getConnectionMeshes(child, connectionNames));
      });
    }
    return connectionMeshes;
  }

  public static cloneMetaElement(name: string, metaElement: MetaElementVO): MetaElementVO {
    if (!metaElement) {
      return null;
    }
    const newMetaElement: MetaElementVO = CloneUtils.cloneMetaElementVO(metaElement);
    newMetaElement.name = name;
    return newMetaElement;
  }



  public static calculateRectangleDistance(rectangle1: Rectangle3D, rectangle2: Rectangle3D): number {
    const distance = Math.min(rectangle1.point1.distanceTo(rectangle2.point4), rectangle1.point4.distanceTo(rectangle2.point1));
    return distance;
  }

  public static findMainConnectionIgnoringPostfix(connections: Connection[], connectionNameWithoutPostfix: string): Connection {
    for (const connection of connections) {
      if (this.isMainMetaElementIgnoringPostfix(connection.metaElementVO, connectionNameWithoutPostfix)) {
        return connection;
      }
    }
    return null;
  }

  public static findMainMetaElementIgnoringPostfix(metaElements: MetaElementVO[], connectionNameWithoutPostfix: string): MetaElementVO {
    for (const metaElement of metaElements) {
      if (this.isMainMetaElementIgnoringPostfix(metaElement, connectionNameWithoutPostfix)) {
        return metaElement;
      }
    }
    return null;
  }

  private static isMainMetaElementIgnoringPostfix(metaElement: MetaElementVO, connectionNameWithoutPostfix: string): boolean {
    return (
      metaElement && !metaElement.isInternal && MetaElementUtils.removeInternalConnectionPostfix(metaElement.name) === connectionNameWithoutPostfix
    );
  }

  public static findLinkedConnectionsInSource(sourceConnection: Connection, sourceConnections: Connection[]): Connection[] {
    const sourceMultipurposeSetId: string = MetaElementUtils.getMultipurposeConnectionSetId(sourceConnection.metaElementVO);
    return !sourceMultipurposeSetId
      ? [sourceConnection]
      : sourceConnections.filter((sc) => MetaElementUtils.getMultipurposeConnectionSetId(sc.metaElementVO) === sourceMultipurposeSetId);
  }

  public static modifyTargetBySourceConnection(targetConnection: Connection, sourceConnection: Connection): void {
    const connectionMesh: Mesh = MeshUtils.cloneMesh(sourceConnection.mesh);
    connectionMesh.name = targetConnection.metaElementVO.name;

    const sourceMeshWidthOrRadius: number = sourceConnection.meshIsCircle ? sourceConnection.meshRadius : sourceConnection.meshWidth;
    const sourceMeshLength: number = sourceConnection.meshIsCircle ? null : sourceConnection.meshLength;

    targetConnection.clearMeshValues();
    targetConnection.mesh.geometry = connectionMesh.geometry;
    targetConnection.mesh.material = connectionMesh.material;
    targetConnection.setDefaultFillColor((targetConnection.mesh.material as MeshPhongMaterial).color.getHex());
    targetConnection.mesh.position.copy(new Vector3());
    targetConnection.mesh.rotation.copy(new Euler());
    targetConnection.updateMesh(sourceConnection.meshPosition, sourceConnection.meshRotationDegree, sourceMeshWidthOrRadius, sourceMeshLength);

    targetConnection.metaElementVO.corridor = sourceConnection.metaElementVO.corridor;
  }

  public static getConnectionImage(connection: Connection): any {
    if (!connection) {
      return;
    }
    const material = connection.getDefaultMaterial();
    if (IsThreeJsType.meshPhongMaterial(material) && material.map) {
      return material.map.image || null;
    }
    return null;
  }

  public static excludeInternalConnectionProperties(properties: PropertyVO[]): PropertyVO[] {
    return properties.filter((p) => !PropertyUtils.isInternalConnectionPropertyName(p.name));
  }

  public static replaceTargetBySourceMetaData(targetMeta: MetaElementVO, sourceMeta: MetaElementVO, isUndoOperation: boolean = false): void {
    this.replacementConnectionPropertiesHandler(targetMeta.behaviorProperties, sourceMeta.behaviorProperties, isUndoOperation);
    if (targetMeta instanceof ConnectionAreaVO && sourceMeta instanceof ConnectionAreaVO) {
      if (targetMeta.connectionSide === ConnectionAreaVO.SIDE_ACCEPTING) {
        this.replacementConnectionPropertiesHandler(targetMeta.connectionRequirements, sourceMeta.connectionRequirements, isUndoOperation);
      } else {
        this.replacementConnectionPropertiesHandler(targetMeta.connectionFeatures, sourceMeta.connectionFeatures, isUndoOperation);
      }
      this.replacementConnectionPropertiesHandler(targetMeta.connectionPlausiProperties, sourceMeta.connectionPlausiProperties, isUndoOperation);
    }
  }

  private static replacementConnectionPropertiesHandler(targetProperties: PropertyVO[], sourceProperties: PropertyVO[], isUndoOperation: boolean): void {
    const internalProperties: PropertyVO[] = targetProperties.filter((p) => PropertyUtils.isInternalConnectionPropertyName(p.name));
    MetaElementUtils.replacementPropertiesHandler(targetProperties, this.excludeInternalConnectionProperties(sourceProperties), isUndoOperation);
    internalProperties.forEach((internalProperty) => {
      PropertyUtils.setProperty(targetProperties, internalProperty);
    });
  }

  public static getMetaElementType(metaElement: MetaElementVO): string {
    if (metaElement instanceof TextureAreaVO) {
      return (metaElement as TextureAreaVO).textureType;
    } else if (metaElement instanceof InteractionAreaVO) {
      return Connection.TYPE_INTERACTION;
    } else if (metaElement instanceof LightingAreaVO) {
      return Connection.TYPE_LIGHTING;
    } else if (metaElement instanceof ReflectionAreaVO) {
      return Connection.TYPE_REFLECTION;
    } else if (metaElement instanceof MontagePointAreaVO) {
      return Connection.TYPE_MONTAGE_POINT;
    } else {
      return Connection.TYPE_ARTICLE;
    }
  }

  public static findLinkedMultipurposeConnection(connection: Connection): Connection {
    if (!connection) {
      return null;
    }
    const articleContainer = connection.articleContainer;
    if (!articleContainer) {
      return null;
    }
    const linkedMetaElement: MetaElementVO = this.findLinkedMultipurposeMetaElement(articleContainer.article, connection.metaElementVO);
    return !linkedMetaElement ? null : articleContainer.findConnectionByName(linkedMetaElement.name);
  }

  public static isActiveConnection(connection: Connection, isAdminView: boolean): boolean {
    if (!connection) {
      return false;
    }
    let isActive: boolean = true;
    const linked: Connection = this.findLinkedMultipurposeConnection(connection);
    if (linked) {
      if (connection.metaElementVO instanceof TextureAreaVO) {
        // at least one accessory article should be connected to treat linked connection as active
        isActive = MetaElementUsageUtils.isFreeConnectionUsage(linked.metaElementUsage, 1) || isAdminView;
      } else if (connection.metaElementVO instanceof ConnectionAreaVO) {
        // at least one accessory article should be connected to treat given connection as active
        isActive = !MetaElementUsageUtils.isFreeConnectionUsage(connection.metaElementUsage, 1) || isAdminView;
      }
    }
    return isActive;
  }

  public static findSuitableAcceptingConnections(article: ArticleVO, articleContainer: ArticleContainer[]): Connection[] {
    const result: Connection[] = [];

    const freeAcceptingCubes = this.getFilteredAcceptingCubesByArticleContainers(articleContainer, true);
    for (const freeCube of freeAcceptingCubes) {
      const acceptingProperties: PropertyVO[] = this.createRequirementPropertyList(
        freeCube.metaElementVO as ConnectionAreaVO,
        freeCube.articleContainer.article
      );
      for (const metaElement of article.metaElements) {
        if (metaElement instanceof ConnectionAreaVO && metaElement.connectionSide === ConnectionAreaVO.SIDE_GIVING) {
          const givingProperties: PropertyVO[] = this.createFeaturePropertyList(metaElement, article);
          if (PropertyUtils.checkProperties(givingProperties, acceptingProperties)) {
            result.push(freeCube);
            break;
          }
        }
      }
    }
    return result;
  }

  public static getArticleContainersByConnectionCubes(connectionCubes: Connection[]): ArticleContainer[] {
    if (ArrayUtils.isEmpty(connectionCubes)) {
      return [];
    }

    const articleContainers: ArticleContainer[] = [];
    // used to avoid duplicates in result-list since given connection-cubes could belong to the same article-container
    const processedContainerGUIDs: string[] = [];
    for (const connectionCube of connectionCubes) {
      const articleContainer: ArticleContainer = connectionCube.articleContainer;
      if (articleContainer && processedContainerGUIDs.indexOf(articleContainer.articleGUID) < 0) {
        articleContainers.push(articleContainer);
        processedContainerGUIDs.push(articleContainer.articleGUID);
      }
    }
    return articleContainers;
  }

  /**
   * Returns connection cubes associated with given meta elements.
   *
   * @param article article container which contains given meta elements
   * @param metaElements meta elements
   * @param connectionTypes only specific types of connections will be included in result array
   * @return connection cubes
   */
  public static getConnectionCubesByMetaElements(article: ArticleContainer, metaElements: MetaElementVO[], connectionTypes: string[] = null): Connection[] {
    const connectionCubes: Connection[] = [];
    for (const meta of metaElements) {
      const cube: Connection = article.findFirstConnectionByName(meta.name);
      if (cube && (!connectionTypes || connectionTypes.indexOf(cube.type) >= 0)) {
        connectionCubes.push(cube);
      }
    }
    return connectionCubes;
  }

  public static getTextConnectionCubesByMetaElements(article: ArticleContainer, metaElements: MetaElementVO[]): Connection[] {
    return this.getConnectionCubesByMetaElements(article, metaElements, Connection.TEXT_TYPES);
  }

  public static getFilteredAcceptingCubesByArticleContainers(articleContainers: ArticleContainer[], ignoreMultiplicity: boolean = false): Connection[] {
    const connectionCubes: Connection[] = this.findConnectionsInArticles(articleContainers);
    return this.filterAcceptingCubes(connectionCubes, ignoreMultiplicity);
  }

  public static getFirstGivingConnection(articleContainer: ArticleContainer): Connection {
    const givingConnections: Connection[] = this.getGivingConnections([articleContainer]);
    return givingConnections.length === 0 ? null : givingConnections[0];
  }

  public static getGivingConnections(articleContainers: ArticleContainer[]): Connection[] {
    return this.getConnections(articleContainers, ConnectionAreaVO.SIDE_GIVING);
  }

  public static isDefaulLabelingConnectionCubes(connectionCubes: Connection[]): boolean {
    if (!connectionCubes) {
      return false;
    }
    for (const connectionCube of connectionCubes) {
      if (!this.isPartOfDefaultLabeling(connectionCube.metaElementVO)) {
        return false;
      }
    }
    return true;
  }

  public static getDeltaDistance(metaElement: MetaElementVO): number {
    if (!metaElement) {
      return NaN;
    }
    return PropertyUtils.getIntPropertyValue(metaElement.behaviorProperties, MetaElementBehaviorPropertyDef.DELTA_DISTANCE);
  }

  public static getLevelNumber(metaElement: MetaElementVO): number {
    if (!metaElement) {
      return NaN;
    }
    return PropertyUtils.getIntPropertyValue(metaElement.behaviorProperties, MetaElementBehaviorPropertyDef.LEVEL_NUMBER);
  }

  public static getCalculatedLevelNumber(metaElement: MetaElementVO): number {
    if (!metaElement) {
      return NaN;
    }
    return PropertyUtils.getIntPropertyValue(metaElement.behaviorProperties, MetaElementBehaviorPropertyDef.CALCULATED_LEVEL_NUMBER);
  }

  public static getConnectionAlternativeGroupProperty(connection: ConnectionAreaVO): PropertyVO {
    if (!connection) {
      return null;
    }
    return PropertyUtils.getPropertyByName(connection.connectionFeatures, ArticleConnectionPropertyDef.ARTICLE_ALTERNATIVE_GROUP);
  }

  public static getFreeBusbarConnections(articleContainer: ArticleContainer, ignoreMultiplicity: boolean = true): Connection[] {
    const acceptingConnections: Connection[] = this.getFilteredAcceptingCubesByArticleContainers([articleContainer], ignoreMultiplicity);
    return this.getBusbarConnectionsFromAcceptingConnections(acceptingConnections);
  }

  public static isPEJumperConnection(metaElement: MetaElementVO): boolean {
    if (!metaElement) {
      return false;
    }
    return PropertyUtils.getBooleanPropertyValue(metaElement.behaviorProperties, MetaElementBehaviorPropertyDef.IS_PE_JUMPER_CONNECTION);
  }

  public static isNPotentialJumperConnection(metaElement: MetaElementVO): boolean {
    if (!metaElement) {
      return false;
    }
    return PropertyUtils.getBooleanPropertyValue(metaElement.behaviorProperties, MetaElementBehaviorPropertyDef.IS_N_POTENTIAL_JUMPER_CONNECTION);
  }

  private static getBusbarConnectionsFromAcceptingConnections(acceptingConnections: Connection[]) {
    const busbarConnections: Connection[] = [];
    for (const connection of acceptingConnections) {
      if (!(connection.metaElementVO as ConnectionAreaVO)) {
        continue;
      }
      const typeName: string = PropertyUtils.getStringPropertyValue(
        (connection.metaElementVO as ConnectionAreaVO).connectionRequirements,
        ArticleConnectionPropertyDef.TYPE
      );
      if (typeName === ArticleConnectionPropertyValue.N_BUSBAR_CONNECTION_TYPE) {
        busbarConnections.push(connection);
      }
    }
    return busbarConnections;
  }

  public static findTextConnections(container: ArticleContainer): Connection[] {
    return this.findConnectionsInArticles([container])
      .filter(connection =>
        Connection.TEXT_TYPES.includes(connection.type) && connection.isActive
      );
  }
}
